/**
 * 日焼けケア記録の画面。
 *
 * 「日焼けした → ケアした → どうなったか」を1件として残し、
 * 自分に合うやり方を見つけられるようにする。
 *
 * 撮影は「画面フラッシュ」方式：シャッターの直前に画面全体を白く光らせ、
 * その明かりで顔を照らしてから1コマ取り込む。毎回同じ光源になるので、
 * ケア前後・日をまたいだ比較ができる。
 * （Webからは端末のバックライト輝度を変えられないため、白い面を出すのが最善手）
 *
 * 写真（顔・食事・レシート）は端末内（IndexedDB）にのみ保存する。
 * レシートだけは読み取りのためサーバのVLMへ送るが、推論後に即破棄する（§8.4と同じ扱い）。
 */
import {
  ACTIVITY_NOTE,
  CARE_DISCLAIMER,
  CARE_TAGS,
  FLASH_NOTE,
  FOOD_TAGS,
  RECEIPT_NOTE,
  SUNBURN_LEVELS,
  describeSunburn,
  prorateReceipt,
  summarizeCare,
} from "@uvk/shared";
import type {
  ActivityRecord,
  CareEntry,
  CareTagId,
  FoodTagId,
  ReceiptItem,
  SunburnLevel,
} from "@uvk/shared";
import { db } from "../db.js";
import { currentUser, state } from "../state.js";
import { accountGateHtml, bindAccount } from "./account.js";
import { scanReceipt } from "../api.js";
import { esc } from "../ui.js";

const FLASH_MS = 700; // 白画面を出してからカメラの露出が落ち着くまでの待ち

let entries: CareEntry[] = [];
/** どのユーザーぶんを読み込み済みか。ユーザーを切り替えたら読み直す */
let loadedFor: string | null | undefined;
let stream: MediaStream | null = null;
/** いま撮っているのがケア前かケア後か */
let shotSlot: "before" | "after" = "before";
let scanning = false;

interface Draft {
  photo: Blob | null;
  photoAfter: Blob | null;
  flashUsed: boolean;
  level: SunburnLevel;
  foods: Set<FoodTagId>;
  cares: Set<CareTagId>;
  mealPhotos: Blob[];
  receiptItems: ReceiptItem[];
  activity: ActivityRecord;
}

function emptyDraft(): Draft {
  return {
    photo: null,
    photoAfter: null,
    flashUsed: false,
    level: 1,
    foods: new Set(),
    cares: new Set(),
    mealPhotos: [],
    receiptItems: [],
    activity: { sleepHours: null, steps: null, exerciseMin: null, source: "manual" },
  };
}
let draft = emptyDraft();

/**
 * ログイン中のユーザーの記録だけを読む。
 * 端末を家族で使い分けるため、他の人の記録・写真は画面に出さない。
 */
async function loadEntries(): Promise<void> {
  const uid = state.currentUserId;
  entries = (await db.getAll<CareEntry>("care_log"))
    .filter((e) => e.userId === uid)
    .sort((a, b) => b.ts - a.ts);
  loadedFor = uid;
}

/** 画面を白く光らせている間にビデオから1コマ取り込む */
async function captureWithScreenFlash(video: HTMLVideoElement): Promise<Blob | null> {
  const overlay = document.createElement("div");
  overlay.style.cssText =
    "position:fixed;inset:0;background:#fff;z-index:9999;display:flex;" +
    "align-items:center;justify-content:center;color:#9ca3af;font-size:14px";
  overlay.textContent = "撮影中…";
  document.body.appendChild(overlay);
  try {
    await new Promise((r) => setTimeout(r, FLASH_MS));
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    // インカメラは鏡像で表示されるため、保存時は左右を戻す
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", 0.85));
  } finally {
    overlay.remove();
  }
}

function stopCamera(): void {
  stream?.getTracks().forEach((t) => t.stop());
  stream = null;
}

function levelLabel(level: number): string {
  return SUNBURN_LEVELS.find((l) => l.value === level)?.label ?? "";
}

function thumb(blob: Blob | null, label: string): string {
  return `<div style="text-align:center">
    <div style="width:88px;height:88px;border-radius:10px;overflow:hidden;background:#f3f4f6;
      display:flex;align-items:center;justify-content:center;color:#9ca3af;font-size:11px">
      ${blob ? `<img src="${URL.createObjectURL(blob)}" style="width:100%;height:100%;object-fit:cover" />` : "未撮影"}
    </div>
    <div class="sub">${label}</div>
  </div>`;
}

export async function renderCare(el: HTMLElement): Promise<void> {
  // ケアの記録はユーザーごとに分けて持つので、誰の記録かが決まるまで先へ進めない
  const user = currentUser();
  if (!user) {
    stopCamera();
    el.innerHTML = accountGateHtml();
    bindAccount(el, () => void renderCare(el));
    return;
  }
  if (loadedFor !== state.currentUserId) await loadEntries();
  const summary = summarizeCare(entries);
  const todaySed = state.engine.sAct;
  const a = draft.activity;

  el.innerHTML = `
    <div class="card">
      <h2>📸 ケア前後を撮る</h2>
      <p class="sub">${esc(user.name)} の記録として残します（設定でユーザーを切り替えられます）</p>
      <p class="sub">${esc(FLASH_NOTE)}</p>
      <div class="row" style="justify-content:center;gap:16px;margin:10px 0">
        ${thumb(draft.photo, "ケア前")}
        ${thumb(draft.photoAfter, "ケア後")}
      </div>
      <video id="cam" playsinline muted hidden style="width:100%;border-radius:10px"></video>
      <div class="row">
        <button id="shoot-before" class="ghost grow">📷 ケア前を撮る</button>
        <button id="shoot-after" class="ghost grow">📷 ケア後を撮る</button>
      </div>
      <div class="row" style="margin-top:6px">
        <button id="capture" class="primary grow" hidden>💡 光らせて撮る</button>
      </div>
    </div>

    <div class="card">
      <h2>🧴 何をしたか</h2>
      <label class="field">日焼けの様子（自分の感じ方）</label>
      <div class="row" id="level-row">
        ${SUNBURN_LEVELS.map(
          (l) => `<button class="ghost ${draft.level === l.value ? "selected" : ""}" data-level="${l.value}">${l.label}</button>`,
        ).join("")}
      </div>

      <label class="field">やったケア</label>
      <div class="row" id="care-row">
        ${CARE_TAGS.map(
          (c) => `<button class="ghost ${draft.cares.has(c.id) ? "selected" : ""}" data-care="${c.id}">${c.label}</button>`,
        ).join("")}
      </div>

      <label class="field">食べたもの</label>
      <div class="row" id="food-row">
        ${FOOD_TAGS.map(
          (f) => `<button class="ghost ${draft.foods.has(f.id) ? "selected" : ""}" data-food="${f.id}" title="${esc(f.examples)}">${f.label}</button>`,
        ).join("")}
      </div>
      <div class="row" style="margin-top:8px">
        <label class="ghost" style="padding:10px 14px;border-radius:10px;border:1px solid var(--line)">
          🍽 食事の写真を追加<input type="file" id="meal-pick" accept="image/*" hidden />
        </label>
        <span class="sub">${draft.mealPhotos.length}枚</span>
      </div>

      <label class="field">レシートから読み取る</label>
      <p class="sub">${esc(RECEIPT_NOTE)}</p>
      <div class="row">
        <label class="ghost" style="padding:10px 14px;border-radius:10px;border:1px solid var(--line)">
          ${scanning ? "読み取り中…" : "🧾 レシートを撮る"}
          <input type="file" id="receipt-pick" accept="image/*" hidden />
        </label>
        <span class="sub">世帯 ${state.localPrefs.householdSize ?? 1}人で案分</span>
      </div>
      ${
        draft.receiptItems.length > 0
          ? `<div class="sub" style="margin-top:6px">${draft.receiptItems
              .map((i) => `${esc(i.name)}（1人あたり${i.perPerson.toFixed(1)}）`)
              .join("、")}</div>`
          : ""
      }

      <label class="field">運動・睡眠</label>
      <p class="sub">${esc(ACTIVITY_NOTE)}</p>
      <div class="row">
        <input type="number" id="sleep" placeholder="睡眠h" min="0" max="24" step="0.5" value="${a.sleepHours ?? ""}" style="width:90px" />
        <input type="number" id="steps" placeholder="歩数" min="0" step="100" value="${a.steps ?? ""}" style="width:110px" />
        <input type="number" id="exercise" placeholder="運動分" min="0" step="5" value="${a.exerciseMin ?? ""}" style="width:90px" />
      </div>

      <label class="field">メモ</label>
      <input type="text" id="care-note" placeholder="例：日傘を忘れた" />

      <button id="care-save" class="primary" style="width:100%;margin-top:10px">この内容で記録する</button>
      <p class="sub">今日の日焼け量 ${describeSunburn(todaySed).poolTime}／大気汚染 PM2.5 ${(state.engine.pm25ExposureMin / Math.max(1, state.engine.minOutdoor)).toFixed(0)} μg/m³ も一緒に残ります</p>
    </div>

    <div class="card">
      <h2>📈 自分の傾向 <span class="sub">記録${summary.total}件</span></h2>
      ${
        summary.total === 0
          ? `<p class="sub">記録がたまると、どのケアをした日が軽かったかを並べて見られます</p>`
          : `<p class="sub">日焼けの自己申告の平均：${summary.overallAvg.toFixed(1)}（0=変化なし〜3=強い赤み）</p>
             ${trendList("やったケア", summary.cares)}
             ${trendList("食べたもの", summary.foods)}
             <p class="sub">※ 件数が少ないうちは偶然の差が出ます。因果ではなく傾向として見てください</p>`
      }
    </div>

    <div class="card">
      <h2>🗂 記録</h2>
      ${
        entries.length === 0
          ? `<p class="sub">まだありません</p>`
          : entries
              .slice(0, 12)
              .map(
                (e) => `
        <div style="padding:8px 0;border-bottom:1px solid var(--line)">
          <div class="row">
            <b class="grow">${new Date(e.ts).toLocaleDateString("ja-JP")} ${esc(levelLabel(e.level))}</b>
            ${e.flashUsed ? `<span class="pill">同条件</span>` : ""}
            <button class="ghost" data-del="${e.ts}">削除</button>
          </div>
          <div class="row" style="gap:8px;margin:6px 0">
            ${thumb(e.photo, "前")}
            ${thumb(e.photoAfter, "後")}
          </div>
          <div class="sub">${describeSunburn(e.sedThatDay).poolTime}／${esc(
            [
              ...e.cares.map((c) => CARE_TAGS.find((t) => t.id === c)?.label ?? ""),
              ...e.foods.map((f) => FOOD_TAGS.find((t) => t.id === f)?.label ?? ""),
            ].join("、") || "記録なし",
          )}</div>
          ${e.note ? `<div class="sub">${esc(e.note)}</div>` : ""}
        </div>`,
              )
              .join("")
      }
    </div>

    <p class="credit">${esc(CARE_DISCLAIMER)}</p>
  `;

  const video = el.querySelector<HTMLVideoElement>("#cam")!;
  const captureBtn = el.querySelector<HTMLButtonElement>("#capture")!;

  const startCamera = async (slot: "before" | "after") => {
    shotSlot = slot;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 720 } },
        audio: false,
      });
      video.srcObject = stream;
      video.hidden = false;
      await video.play();
      captureBtn.hidden = false;
      captureBtn.textContent = `💡 光らせて${slot === "before" ? "ケア前" : "ケア後"}を撮る`;
    } catch {
      alert("カメラを使えませんでした（この端末・ブラウザでは撮影できません）");
    }
  };

  el.querySelector<HTMLButtonElement>("#shoot-before")!.onclick = () => void startCamera("before");
  el.querySelector<HTMLButtonElement>("#shoot-after")!.onclick = () => void startCamera("after");

  captureBtn.onclick = async () => {
    captureBtn.disabled = true;
    const blob = await captureWithScreenFlash(video);
    captureBtn.disabled = false;
    if (!blob) return;
    if (shotSlot === "before") draft.photo = blob;
    else draft.photoAfter = blob;
    draft.flashUsed = true;
    stopCamera();
    await renderCare(el);
  };

  el.querySelector<HTMLInputElement>("#meal-pick")!.onchange = async (ev) => {
    const f = (ev.target as HTMLInputElement).files?.[0];
    if (f) draft.mealPhotos.push(f);
    await renderCare(el);
  };

  el.querySelector<HTMLInputElement>("#receipt-pick")!.onchange = async (ev) => {
    const f = (ev.target as HTMLInputElement).files?.[0];
    if (!f) return;
    scanning = true;
    await renderCare(el);
    const items = await scanReceipt(f);
    scanning = false;
    if (items.length === 0) {
      alert("レシートを読み取れませんでした");
    } else {
      draft.receiptItems = prorateReceipt(items, state.localPrefs.householdSize ?? 1);
      // 読み取れたカテゴリは「食べたもの」にも反映（ユーザーが外せる）
      for (const i of draft.receiptItems) if (i.category) draft.foods.add(i.category);
    }
    await renderCare(el);
  };

  el.querySelectorAll<HTMLButtonElement>("#level-row [data-level]").forEach((b) => {
    b.onclick = () => {
      draft.level = Number(b.dataset.level) as SunburnLevel;
      void renderCare(el);
    };
  });
  el.querySelectorAll<HTMLButtonElement>("#food-row [data-food]").forEach((b) => {
    b.onclick = () => {
      const id = b.dataset.food as FoodTagId;
      draft.foods.has(id) ? draft.foods.delete(id) : draft.foods.add(id);
      void renderCare(el);
    };
  });
  el.querySelectorAll<HTMLButtonElement>("#care-row [data-care]").forEach((b) => {
    b.onclick = () => {
      const id = b.dataset.care as CareTagId;
      draft.cares.has(id) ? draft.cares.delete(id) : draft.cares.add(id);
      void renderCare(el);
    };
  });

  el.querySelector<HTMLButtonElement>("#care-save")!.onclick = async () => {
    const num = (id: string): number | null => {
      const v = el.querySelector<HTMLInputElement>(id)!.value;
      return v === "" ? null : Number(v);
    };
    const outdoorMin = Math.max(1, state.engine.minOutdoor);
    const user = currentUser();
    if (!user) return; // 未ログインならフォーム自体を出していないが、念のため
    const entry: CareEntry = {
      ts: Date.now(),
      userId: user.id,
      photo: draft.photo,
      photoAfter: draft.photoAfter,
      flashUsed: draft.flashUsed,
      level: draft.level,
      foods: [...draft.foods],
      cares: [...draft.cares],
      mealPhotos: draft.mealPhotos,
      receiptItems: draft.receiptItems,
      activity: {
        sleepHours: num("#sleep"),
        steps: num("#steps"),
        exerciseMin: num("#exercise"),
        source: "manual",
      },
      note: el.querySelector<HTMLInputElement>("#care-note")!.value.slice(0, 200),
      sedThatDay: state.engine.sAct,
      pm25ThatDay: state.engine.pm25ExposureMin / outdoorMin,
    };
    await db.put("care_log", entry);
    entries.unshift(entry);
    draft = emptyDraft();
    stopCamera();
    await renderCare(el);
  };

  el.querySelectorAll<HTMLButtonElement>("[data-del]").forEach((b) => {
    b.onclick = async () => {
      const ts = Number(b.dataset.del);
      await db.delete("care_log", ts);
      entries = entries.filter((e) => e.ts !== ts);
      await renderCare(el);
    };
  });
}

function trendList(title: string, list: { label: string; avgLevel: number; count: number }[]): string {
  if (list.length === 0) return "";
  return `
    <p class="sub" style="margin-bottom:4px">${title}（軽かった順）</p>
    ${list
      .map(
        (t) => `<div class="row" style="font-size:13px;padding:2px 0">
          <span class="grow">${esc(t.label)}</span>
          <span class="sub">平均 ${t.avgLevel.toFixed(1)} ／ ${t.count}件</span>
        </div>`,
      )
      .join("")}`;
}

/** タブを離れるときにカメラを止める */
export function destroyCare(): void {
  stopCamera();
}
